<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { CheckCircle2, ChevronRight, Circle, ExternalLink, LoaderCircle, MinusCircle, RefreshCw } from 'lucide-vue-next';
import Button from '@/volt/Button.vue';
import { apiFetch } from '@/services/api';
import { firebaseConfigured } from '@/services/firebase';
import type { InstallationTestStatus } from '@/domain/installation-guide';

/**
 * Shared "Prüfen" step of every setup flow. It owns the short-lived
 * installation test, polls its status and reports it to the parent.
 */
const props = defineProps<{ siteId: string; domain: string; scheme?: 'http' | 'https' }>();
const includeConsent = defineModel<boolean>('includeConsent', { default: false });
const status = defineModel<InstallationTestStatus | undefined>('status');
const test = ref<{ id: string; token: string; expiresAt: string }>();
const busy = ref(false);
const error = ref('');
let sequence = 0;
let polling = false;
let pollTimer: number | undefined;

function clearPolling() { if (pollTimer) clearInterval(pollTimer); pollTimer = undefined; }
const testUrl = computed(() => {
  if (!test.value) return '';
  const payload = btoa(JSON.stringify({ siteId: props.siteId, testId: test.value.id, token: test.value.token }));
  return `${props.scheme ?? 'https'}://${props.domain}/#moonshot-install-test=${encodeURIComponent(payload)}`;
});
const terminal = computed(() => status.value?.status === 'expired' || status.value?.status === 'failed');
const passed = computed(() => status.value?.status === 'passed');
// Demo mode has no backend to poll.
const livePolling = firebaseConfigured;

type RowState = 'ok' | 'pending' | 'skipped';
const consentDetail = computed(() => {
  if (!includeConsent.value) return 'Nur für Besucher-Funktionen nötig.';
  const consent = status.value?.consent;
  if (consent?.accepted && consent.withdrawn) return consent.replayReceived ? 'Zustimmung, Widerruf und Video-Daten empfangen.' : 'Zustimmung und Widerruf empfangen.';
  if (consent?.accepted) return 'Zustimmung empfangen – jetzt im Banner widerrufen.';
  return 'Wartet auf deine Zustimmung im Banner.';
});
const rows = computed(() => {
  const checks = status.value?.checks;
  const state = (value?: boolean): RowState => value ? 'ok' : 'pending';
  return [
    ...(includeConsent.value ? [{ id: 'tag', label: 'Tag eingebunden', detail: 'Der Tag lädt auf deiner veröffentlichten Website.', state: state(checks?.browser) }] : []),
    { id: 'data', label: 'Daten kommen an', detail: 'Moonshot empfängt den Testaufruf deiner Website.', state: state(checks?.edge) },
    { id: 'consent', label: 'Cookie-Banner verbunden (optional)', detail: consentDetail.value, state: includeConsent.value ? state(checks?.consent) : 'skipped' as RowState }
  ];
});
const stateLabel: Record<RowState, string> = { ok: 'OK', pending: 'Ausstehend', skipped: 'Nicht geprüft' };
const stateIcon = { ok: CheckCircle2, pending: Circle, skipped: MinusCircle };

async function startTest() {
  if (busy.value) return;
  clearPolling(); status.value = undefined;
  const current = ++sequence;
  busy.value = true; error.value = '';
  try {
    const created = firebaseConfigured
      ? await apiFetch<{ id: string; token: string; expiresAt: string }>(`/admin/sites/${encodeURIComponent(props.siteId)}/installation-tests`, { method: 'POST', body: JSON.stringify({ stack: 'other', desiredModes: includeConsent.value ? ['account', 'visitor'] : ['account'] }) })
      : { id: '0123456789abcdef01234567', token: 'demo-token-demo-token-demo-token-123', expiresAt: new Date(Date.now() + 1_800_000).toISOString() };
    if (current !== sequence) return;
    test.value = created;
    if (firebaseConfigured) pollTimer = window.setInterval(() => { void refresh(false); }, 2_500);
  } catch (reason) {
    if (current === sequence) error.value = reason instanceof Error ? reason.message : 'Installationstest konnte nicht gestartet werden.';
  } finally { if (current === sequence) busy.value = false; }
}
async function refresh(showSpinner = true) {
  if (!test.value || !firebaseConfigured || polling) return;
  const current = sequence; polling = true;
  if (showSpinner) busy.value = true;
  try {
    const next = await apiFetch<InstallationTestStatus>(`/admin/sites/${encodeURIComponent(props.siteId)}/installation-tests/${encodeURIComponent(test.value.id)}`);
    if (current !== sequence) return;
    status.value = next; error.value = '';
    if (['passed', 'expired', 'failed'].includes(next.status)) clearPolling();
  } catch (reason) {
    if (current === sequence) error.value = reason instanceof Error ? reason.message : 'Teststatus konnte nicht geladen werden.';
  } finally { polling = false; if (current === sequence && showSpinner) busy.value = false; }
}
watch(() => props.siteId, () => { sequence++; clearPolling(); test.value = undefined; status.value = undefined; busy.value = false; });
onBeforeUnmount(() => { sequence++; clearPolling(); });
</script>

<template>
  <section class="setup-step verify-step" aria-labelledby="verify-step-title" :aria-busy="busy">
    <header class="setup-step__intro">
      <h3 id="verify-step-title">Verbindung prüfen</h3>
      <p>Starte den Test und öffne deine veröffentlichte Website über den Prüflink. Moonshot prüft dann automatisch, ob alles ankommt.</p>
    </header>
    <ul class="verify-checklist">
      <li v-for="row in rows" :key="row.id" :class="`is-${row.state}`">
        <component :is="stateIcon[row.state]" :size="18" aria-hidden="true" />
        <span><strong>{{ row.label }}</strong><small>{{ row.detail }}</small></span>
        <em>{{ stateLabel[row.state] }}</em>
      </li>
    </ul>
    <label class="verify-step__option"><input v-model="includeConsent" type="checkbox" :disabled="Boolean(test)" /> Auch Cookie-Banner prüfen (Zustimmung und Widerruf)</label>
    <template v-if="includeConsent">
      <ol class="setup-step__how">
        <li>Auf der Testseite im Cookie-Banner zustimmen – für Video und Heatmap auch diesen Zweck.</li>
        <li>Kurz auf der Seite bleiben und die Zustimmung danach im Banner widerrufen.</li>
      </ol>
      <p class="setup-step__muted">Der Test prüft den technischen Ablauf, nicht die rechtliche Wirksamkeit der Einwilligung.</p>
    </template>
    <p v-if="status?.consent?.warning" class="setup-step__error" role="alert">Platzhalter statt echtem Banner erkannt. Bitte den echten Banner-Callback prüfen; ein pauschaler Aufruf beim Seitenladen reicht nicht.</p>

    <div class="verify-step__actions">
      <Button v-if="!test" variant="primary" :disabled="busy" @click="startTest"><LoaderCircle v-if="busy" class="spin" :size="14" /> Installationstest starten</Button>
      <template v-else-if="terminal">
        <p>{{ status?.status === 'expired' ? 'Der Test ist abgelaufen.' : 'Der Test ist fehlgeschlagen.' }}</p>
        <Button variant="primary" :disabled="busy" @click="startTest">Neuen Test starten</Button>
      </template>
      <template v-else>
        <a class="v-button v-button--primary" :href="testUrl" target="_blank" rel="noopener"><ExternalLink :size="14" aria-hidden="true" /> Website prüfen</a>
        <Button variant="ghost" :disabled="busy" @click="refresh()"><LoaderCircle v-if="busy" class="spin" :size="14" /><RefreshCw v-else :size="14" /> Status aktualisieren</Button>
      </template>
    </div>
    <p v-if="passed" class="verify-step__result" role="status">Alles angekommen. Deine Website ist verbunden.</p>
    <p v-else-if="test && !terminal && livePolling" class="setup-step__muted" role="status">Der Status aktualisiert sich automatisch, solange dieses Fenster offen ist.</p>
    <p v-if="error" class="setup-step__error" role="alert">{{ error }}</p>

    <details class="setup-disclosure setup-disclosure--nested">
      <summary><span><strong>Es kommt nichts an?</strong></span><ChevronRight :size="14" aria-hidden="true" /></summary>
      <div class="setup-disclosure__body">
        <p>Prüfe, ob der Tag veröffentlicht ist und dein Website-System externe Scripts und Verbindungen zu Moonshot erlaubt. Hilft das nicht, kontaktiere den Support und nenne diese Site-ID:</p>
        <code class="verify-step__site-id">{{ siteId }}</code>
        <p class="setup-step__muted">Ein bestandener Test garantiert nicht, dass jeder Besuch einer Firma zugeordnet werden kann.</p>
      </div>
    </details>
  </section>
</template>
